import { ResolvableModeEnum } from '@e-socna/data-layer';
import type { ResolverId } from '@e-socna/data-layer';

import type { DocumentEditorCmp_SectionDisplayCondition } from '../fragments/recursive-section.fragment';

export const ALWAYS_DISPLAYED = 'always';
export const NEVER_DISPLAYED = 'never';

// The choice of the display condition of a section: always displayed (no
// condition), never displayed (static false) or the id of a boolean resolver.
export type DisplayConditionChoice =
  typeof ALWAYS_DISPLAYED | typeof NEVER_DISPLAYED | ResolverId;

type DisplayCondition = Omit<
  DocumentEditorCmp_SectionDisplayCondition,
  '__typename'
>;

export const displayConditionChoiceOf = (
  condition: Readonly<DisplayCondition> | null | undefined,
): DisplayConditionChoice => {
  if (!condition) {
    return ALWAYS_DISPLAYED;
  }

  if (condition.mode === ResolvableModeEnum.Static) {
    return condition.value ? ALWAYS_DISPLAYED : NEVER_DISPLAYED;
  }

  return condition.resolverId
    ? (condition.resolverId as ResolverId)
    : ALWAYS_DISPLAYED;
};

export const displayConditionOf = (
  choice: DisplayConditionChoice,
): DocumentEditorCmp_SectionDisplayCondition | null => {
  switch (choice) {
    case ALWAYS_DISPLAYED:
      return null;
    case NEVER_DISPLAYED:
      return {
        __typename: 'ResolvableBoolean',
        mode: ResolvableModeEnum.Static,
        resolverId: null,
        value: false,
      };
    default:
      return {
        __typename: 'ResolvableBoolean',
        mode: ResolvableModeEnum.Dynamic,
        resolverId: choice,
        value: null,
      };
  }
};

export const displayConditionLabelOf = (
  condition: Readonly<DisplayCondition>,
  resolversIdMap: Readonly<Record<ResolverId, Readonly<{ label: string }>>>,
): string => {
  const choice = displayConditionChoiceOf(condition);

  switch (choice) {
    case ALWAYS_DISPLAYED:
      return 'toujours affichée';
    case NEVER_DISPLAYED:
      return 'jamais affichée';
    default:
      return `affichée si « ${resolversIdMap[choice]?.label ?? 'condition inconnue'} »`;
  }
};
